import { gsap } from 'gsap';

export class UIController {
    constructor(app) {
        this.app = app;
        this.currentScene = 1;
        this.isTyping = false;
        
        this.elements = {
            scenes: {
                1: document.getElementById('scene1'),
                2: document.getElementById('scene2'),
                3: document.getElementById('scene3'),
                4: document.getElementById('scene4')
            },
            speechBubble: document.getElementById('speech-bubble'),
            dialogueText: document.getElementById('dialogue-text'),
            continueBtn: document.getElementById('continue-btn'),
            projectTitle: document.getElementById('project-title'),
            projectDescription: document.getElementById('project-description'),
            projectLive: document.getElementById('project-live'),
            projectCode: document.getElementById('project-code'),
            currentProject: document.getElementById('current-project'),
            totalProjects: document.getElementById('total-projects'),
            prevBtn: document.getElementById('prev-project'),
            nextBtn: document.getElementById('next-project')
        };
        
        this.projects = [
            {
                title: "E-Commerce Platform",
                description: "Full-stack e-commerce solution with React, Node.js, and MongoDB. Features include user authentication, payment processing, and admin dashboard.",
                live: "https://demo.example.com",
                code: "https://github.com/username/ecommerce"
            },
            {
                title: "3D Portfolio Website",
                description: "Interactive 3D portfolio built with Three.js and GSAP. Features immersive storytelling and smooth animations.",
                live: "https://portfolio.example.com",
                code: "https://github.com/username/portfolio"
            },
            {
                title: "Task Management App",
                description: "Collaborative task management application with real-time updates using Socket.io and React.",
                live: "https://tasks.example.com",
                code: "https://github.com/username/taskapp"
            },
            {
                title: "Weather Dashboard",
                description: "Beautiful weather dashboard with interactive charts and location-based forecasts using React and D3.js.",
                live: "https://weather.example.com",
                code: "https://github.com/username/weather"
            },
            {
                title: "Chat Application",
                description: "Real-time chat application with rooms, file sharing, and emoji support built with React and Firebase.",
                live: "https://chat.example.com",
                code: "https://github.com/username/chat"
            }
        ];
        
        this.currentProjectIndex = 0;
        
        this.init();
    }

    init() {
        if (this.elements.totalProjects) {
            this.elements.totalProjects.textContent = this.projects.length;
        }
        
        this.updateProjectDisplay();
    }

    showScene(sceneNumber) {
        if (this.currentScene === sceneNumber) return;
        
        const previousScene = this.elements.scenes[this.currentScene];
        const nextScene = this.elements.scenes[sceneNumber];
        
        if (previousScene) {
            gsap.to(previousScene, {
                opacity: 0,
                duration: 0.5,
                ease: "power2.inOut",
                onComplete: () => {
                    previousScene.classList.remove('active');
                }
            });
        }
        
        if (nextScene) {
            nextScene.classList.add('active');
            gsap.fromTo(nextScene, 
                { opacity: 0 },
                { 
                    opacity: 1, 
                    duration: 0.5, 
                    ease: "power2.inOut",
                    delay: 0.2
                }
            );
        }
        
        this.currentScene = sceneNumber;
    }

    showDialogue(text, onComplete = null) {
        if (this.isTyping) return;
        
        const bubble = this.elements.speechBubble;
        const textElement = this.elements.dialogueText;
        const continueBtn = this.elements.continueBtn;
        
        if (!bubble || !textElement || !continueBtn) return;
        
        bubble.classList.remove('show');
        continueBtn.classList.remove('show');
        
        setTimeout(() => {
            bubble.classList.add('show');
            this.typeText(text, textElement, () => {
                setTimeout(() => {
                    continueBtn.classList.add('show');
                    if (onComplete) onComplete();
                }, 500);
            });
        }, 300);
    }

    typeText(text, element, onComplete = null) {
        if (this.isTyping) return;
        
        this.isTyping = true;
        element.textContent = '';
        
        let index = 0;
        const typeSpeed = 50;
        
        const typeChar = () => {
            if (index < text.length) {
                element.textContent += text.charAt(index);
                index++;
                setTimeout(typeChar, typeSpeed);
            } else {
                this.isTyping = false;
                if (onComplete) onComplete();
            }
        };
        
        typeChar();
    }

    updateProjectDisplay() {
        const project = this.projects[this.currentProjectIndex];
        
        if (this.elements.projectTitle) {
            this.elements.projectTitle.textContent = project.title;
        }
        
        if (this.elements.projectDescription) {
            this.elements.projectDescription.textContent = project.description;
        }
        
        if (this.elements.projectLive) {
            this.elements.projectLive.href = project.live;
        }
        
        if (this.elements.projectCode) {
            this.elements.projectCode.href = project.code;
        }
        
        if (this.elements.currentProject) {
            this.elements.currentProject.textContent = this.currentProjectIndex + 1;
        }
        
        if (this.elements.prevBtn) {
            this.elements.prevBtn.disabled = this.currentProjectIndex === 0;
        }
        
        if (this.elements.nextBtn) {
            this.elements.nextBtn.disabled = this.currentProjectIndex === this.projects.length - 1;
        }
    }

    nextProject() {
        if (this.currentProjectIndex < this.projects.length - 1) {
            this.currentProjectIndex++;
            this.updateProjectDisplay();
            this.animateProjectChange();
            
            if (this.app.scenes.gallery) {
                this.app.scenes.gallery.showProject(this.currentProjectIndex);
            }
        }
    }

    previousProject() {
        if (this.currentProjectIndex > 0) {
            this.currentProjectIndex--;
            this.updateProjectDisplay();
            this.animateProjectChange();
            
            if (this.app.scenes.gallery) {
                this.app.scenes.gallery.showProject(this.currentProjectIndex);
            }
        }
    }

    animateProjectChange() {
        const projectInfo = document.querySelector('.project-info');
        if (!projectInfo) return;
        
        gsap.fromTo(projectInfo,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
        );
    }

    showNotification(message, duration = 3000) {
        const notification = document.createElement('div');
        notification.className = 'notification';
        notification.textContent = message;
        notification.style.cssText = `
            position: fixed;
            top: 2rem;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 212, 255, 0.9);
            color: white;
            padding: 1rem 2rem;
            border-radius: 50px;
            z-index: 1000;
            font-weight: 500;
            backdrop-filter: blur(10px);
            opacity: 0;
            pointer-events: none;
        `;
        
        document.body.appendChild(notification);
        
        gsap.to(notification, {
            opacity: 1,
            duration: 0.3,
            ease: "power2.out"
        });
        
        setTimeout(() => {
            gsap.to(notification, {
                opacity: 0,
                duration: 0.3,
                ease: "power2.in",
                onComplete: () => {
                    document.body.removeChild(notification);
                }
            });
        }, duration);
    }

    toggleDebugMode() {
        document.body.classList.toggle('debug-mode');
    }
}